import { Component, OnInit, ViewChild } from '@angular/core';
import { Common } from 'src/app/common';
import { SessionService } from 'src/app/services/session.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import { PaymentArrangement } from 'src/app/models/payment-arrangement.model';
import { PaymentPlanService } from 'src/app/services/payment-plan.service';
import { PaymentPlanDetails } from 'src/app/models/payment/payment-plan-details.model';
import { PaymentMethod } from 'src/app/models/payment/payment-method.model';
import { PaymentAccount } from 'src/app/models/payment/payment-account.model';
import { CurrencyExchangeService } from 'src/app/services/currency-exchange.service';
import { forkJoin, map, Observable } from 'rxjs';
import { FormWizardPage, FormWizardPageElement, FormWizardTemplate } from 'src/app/models/form-wizard-template.model';
import { Router } from '@angular/router';
import { ValidatePaymentPlanResult } from 'src/app/models/payment/validate-payment-plan-result.model';
import { InitialisePaymentPlanResult } from 'src/app/models/payment/initialise-payment-plan-result.model';
import { NgForm } from '@angular/forms';
import { CountryService } from 'src/app/services/country.service';
import { Countries } from 'src/app/models/settings/countries.model';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { AppConfigService } from 'src/app/services/app-config.service';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NabCardPaymentDialogComponent } from '../../dialog/nab-card-payment-dialog/nab-card-payment-dialog.component';
import { CreditCardDetails } from 'src/app/models/payment/credit-card-details.model';
import { MwCardPaymentDialogComponent } from '../../dialog/mw-card-payment-dialog/mw-card-payment-dialog.component';
import { MerchantWarriorAddCardResult } from 'src/app/models/payment/merchant-warrior-add-card-result.model';

@Component({
  selector: 'app-pay-by-arrangement',
  templateUrl: './pay-by-arrangement.component.html',
  styleUrls: ['./pay-by-arrangement.component.scss']
})
export class PayByArrangementComponent implements OnInit {
  @ViewChild('paymentPlanForm') public paymentPlanForm: NgForm | undefined;

  private countries = new Countries();
  private paymentPlanDetails = new PaymentPlanDetails();
  private selectedDebtAccountIds: Array<string> = [];
  private selectedPaymentCountry = '';
  public selectedPaymentMethod = '';

  public paymentArrangement = new PaymentArrangement();
  public formWizardTemplate?: FormWizardTemplate;
  public formValues: any = {};
  public paymentPlanSummary = new InitialisePaymentPlanResult();
  public initialised = false;
  public currencyExchangeInitialised = false;
  public showBaseScreen = true;
  public showWizardScreen = false;
  public showConfirmScreen = false;
  public processing = false;

  constructor(
    private paymentPlanService: PaymentPlanService,
    private currencyExchangeService: CurrencyExchangeService,
    private countryService: CountryService,
    private sessionService: SessionService,
    private configurationService: ConfigurationService,
    private appConfigService: AppConfigService,
    private router: Router,
    private dialogService: DialogService) { }

  ngOnInit(): void {
    setTimeout(() => this.initialiseView(), 0);
  }

  private initialiseView(): void {
    this.sessionService.showLoading();

    forkJoin({
      countries: this.countryService.getCountries(),
      paymentPlanDetails: this.paymentPlanService.getPaymentPlanDetails()
    }).subscribe({
      next: (result) => this.initialiseViewOk(result),
      error: (error) => this.initialiseViewError(error.error)
    });
  }

  private initialiseViewOk(result: any) {
    this.paymentPlanDetails = result.paymentPlanDetails;
    this.countries = result.countries;

    this.setDefaultCountry();
    this.setDefaultPaymentMethod();

    this.paymentPlanDetails.accounts.forEach((account) => {
      this.selectedDebtAccountIds.push(account.accountId);
    });

    if (!_.isNil(this.paymentPlanDetails.defaultInstAmt)) {
      this.paymentArrangement.regularPaymentAmount = this.paymentPlanDetails.defaultInstAmt;
    }

    if (!_.isNil(this.paymentPlanDetails.minStartDate) && this.paymentPlanDetails.minStartDate !== '') {
      this.paymentArrangement.regularPaymentStartDate = new Date(this.paymentPlanDetails.minStartDate);
    }

    switch (this.paymentPlanDetails.defaultFrequency) {
      case 'W':
        this.paymentArrangement.regularPaymentFrequencyPeriod = 'Weekly';
        break;
      case 'F':
        this.paymentArrangement.regularPaymentFrequencyPeriod = 'Fortnightly';
        break;
      case 'M':
        this.paymentArrangement.regularPaymentFrequencyPeriod = 'Monthly';
        break;
    }

    this.initialised = true;
    this.sessionService.hideLoading();
  }

  private initialiseViewError(error: any) {
    this.sessionService.hideLoading();
    this.sessionService.showErrorDialog(error.message);

    this.router.navigate(['/']);
  }

  public isDebtAccountSelected(accountId: string): boolean {
    return _.some(this.selectedDebtAccountIds, (id) => id === accountId);
  }

  public toggleDebtAccount(accountId: string): void {
    if (this.isDebtAccountSelected(accountId)) {
      _.remove(this.selectedDebtAccountIds, (id) => id === accountId);
    } else {
      this.selectedDebtAccountIds.push(accountId);
    }
  }

  public getPaymentFrequencyPeriods(): Array<string> {
    return ['Weekly', 'Fortnightly', 'Monthly'];
  }

  public getDebtAccounts(): Array<PaymentAccount> {
    return this.paymentPlanDetails.accounts;
  }

  public getPaymentCountries(): Array<string> {
    return _.map(this.paymentPlanDetails.countries, (country) => country.countryName);
  }

  public getPaymentMethods(): Array<PaymentMethod> {
    return _.find(this.paymentPlanDetails.countries, (country) => country.countryName === this.selectedPaymentCountry)?.payMethods ?? [];
  }

  public getDebtAccountDescription(account: PaymentAccount): string {
    return `${account.portfolioDesc} ${this.formatCurrency(account.totalOutstanding)} ${this.getPaymentCurrency()}`;
  }

  public getPaymentCurrency(): string {
    return this.paymentPlanDetails.accounts.length > 0 ? this.paymentPlanDetails.accounts[0].currency : '';
  }

  private getSelectedTotalAmount(): number {
    let total = 0;

    this.getSelectedDebtAccounts().forEach((debtAccount) => {
      total += debtAccount.totalOutstanding;
    });

    return total;
  }

  private getSelectedDebtAccounts(): Array<PaymentAccount> {
    const result: Array<PaymentAccount> = [];

    this.selectedDebtAccountIds.forEach((debtAccountId) => {
      const selectedDebtAccount = _.find(this.getDebtAccounts(), (debtAccount) => debtAccount.accountId === debtAccountId);

      if (!_.isNil(selectedDebtAccount)) {
        result.push(selectedDebtAccount);
      }
    });

    return result;
  }

  public getSelectedTotal(): string {
    return this.formatCurrency(this.getSelectedTotalAmount());
  }

  public isCountrySelectVisible(): boolean {
    return this.getPaymentCountries().length > 1;
  }

  public isCountrySelected(country: string): boolean {
    return this.selectedPaymentCountry === country;
  }

  public isPaymentMethodSelected(paymentMethod: PaymentMethod): boolean {
    return this.selectedPaymentMethod === paymentMethod.payMethod;
  }

  public isPaymentAmountInMethodCurrencyVisible(): boolean {
    const selectedPaymentMethod = this.getSelectedPaymentMethodObject();
    const paymentCurrency = this.getPaymentCurrency();

    return this.currencyExchangeInitialised && !_.isNil(selectedPaymentMethod) && selectedPaymentMethod.currency != '' && paymentCurrency !== '' &&
      selectedPaymentMethod.currency !== paymentCurrency;
  }

  public getTotalAmountInMethodCurrency(): Observable<string> {
    return this.getAmountInMethodCurrency(this.getSelectedTotalAmount());
  }

  private getAmountInMethodCurrency(amount: number): Observable<string> {
    const paymentMethodCurrency = this.getSelectedPaymentMethodObject()?.currency ?? '';

    return this.currencyExchangeService.convert(this.getPaymentCurrency(), paymentMethodCurrency, amount).pipe(map((
      result: number) => `(${this.formatCurrency(result)} ${paymentMethodCurrency})`));
  }

  public getSelectedPaymentMethodObject(): PaymentMethod | undefined {
    return _.find(this.getPaymentMethods(), (method) => method.payMethod === this.selectedPaymentMethod);
  }

  public getSelectedPaymentMethodCurrency(): string {
    return this.getSelectedPaymentMethodObject()?.currency ?? '';
  }

  public setPaymentCountry(paymentCountry: string): void {
    this.processing = true;
    this.currencyExchangeInitialised = false;
    this.selectedPaymentCountry = paymentCountry;

    this.setDefaultPaymentMethod();
  }

  public setDefaultCountry(): void {
    this.currencyExchangeInitialised = false;

    if (this.paymentPlanDetails.countries.length > 0) {
      this.selectedPaymentCountry = this.paymentPlanDetails.countries[0].countryName;
    } else {
      this.selectedPaymentCountry = '';
    }
  }

  public setDefaultPaymentMethod(): void {
    this.processing = true;
    const paymentMethods = this.getPaymentMethods();
    let defaultPaymentMethod = new PaymentMethod();

    if (paymentMethods.length > 0) {
      defaultPaymentMethod = paymentMethods[0]
    }

    this.setPaymentMethod(defaultPaymentMethod);
  }

  public setPaymentMethod(paymentMethod: PaymentMethod): void {
    const paymentCurrency = this.getPaymentCurrency();

    // When setting the selected payment method, we want to pre cache the exchange rate if applicable, otherwise the view could cause more HTTP calls then required
    if (paymentCurrency !== '' && paymentMethod.currency !== '' && paymentCurrency !== paymentMethod.currency) {
      this.currencyExchangeService.convert(paymentCurrency, paymentMethod.currency, 1).subscribe({
        next: () => {
          this.selectedPaymentMethod = paymentMethod.payMethod;
          this.currencyExchangeInitialised = true;
          this.processing = false;
        },
        error: () => {
          this.sessionService.showErrorDialog('Error retrieving currency exchange information. Please try selecting the payment method again or alternatively try another payment method.');
          this.setDefaultCountry();
          this.setDefaultPaymentMethod();
        }
      })
    } else {
      this.selectedPaymentMethod = paymentMethod.payMethod;
      this.currencyExchangeInitialised = true;
      this.processing = false;
    }
  }

  public isNextDisabled(): boolean {
    return this.selectedDebtAccountIds.length === 0 || this.selectedPaymentMethod === '' || this.processing;
  }

  public onNextClick(): void {
    if (!_.isNil(this.paymentPlanForm) && !this.paymentPlanForm.invalid) {
      this.validate();
    }
  }

  public isCollexusVersion(): boolean {
    return this.appConfigService.getConfig().htmlVersion !== 'version1';
  }

  private isIntercollVersion(): boolean {
    return this.appConfigService.getConfig().htmlVersion === 'version1';
  }

  private isIntercollAustralianVersion(): boolean {
    return this.isIntercollVersion() && this.appConfigService.getConfig().country === 'AU';
  }

  private validate(): void {
    this.sessionService.showProcessing();

    this.paymentPlanService.validatePaymentPlan(this.getFormDataObject()).subscribe({
      next: (result) => this.validateOk(result),
      error: (error) => this.validateError(error.error)
    });
  }

  private validateOk(result: ValidatePaymentPlanResult): void {
    this.sessionService.hideProcessing();

    if (result.valid) {
      this.formWizardTemplate = this.getFormWizardTemplate();
  
      this.showBaseScreen = false;
      this.showWizardScreen = true;
    } else {
      this.sessionService.showErrorDialog(result.reason);
    }
  }

  private validateError(error: any): void {
    this.sessionService.hideProcessing();
    this.sessionService.showErrorDialog(error); 
  }

  public formatCurrency(amount: number): string {
    return Common.getFormattedCurrencyValue(amount, false, false);
  }

  public formWizardActionHandler(action: string): void {
    switch (action) {
      case 'previous':
        this.showWizardScreen = false;
        this.showBaseScreen = true;
        this.formValues = {};
        break;
      case 'complete':
        this.handleFormComplete();
        break;
    }
  }

  private handleFormComplete(): void {
    switch (this.selectedPaymentMethod) {
      case 'CARD':
      case 'MANUAL':
      case 'BPAY':
        this.initialisePaymentPlan();
        break;
      default:
        this.showWizardScreen = false;
        this.showConfirmScreen = true;
        break;
    }
  }

  public getConfirmHeading(): string {
    let result = '';

    switch (this.selectedPaymentMethod) {
      case 'DD':
      default:
        result = 'Confirm payment plan.';
        break;
    }

    return result;
  }

  public getConfirmDebts(): string {
    let result = '';

    this.getSelectedDebtAccounts().forEach((debtAccount) => {
      if (result !== '') {
        result += ', ';
      }

      result += this.getDebtAccountDescription(debtAccount);
    });

    return result;
  }

  public getConfirmTotal(): string {
    return `${this.getSelectedTotal()} ${this.getPaymentCurrency()}`;
  }

  public getPlanSummaryAmount(): string {
    const currency = this.getPaymentCurrency() !== this.getSelectedPaymentMethodCurrency() ? ` ${this.getSelectedPaymentMethodCurrency()}` : '';

    return `${this.formatCurrency(this.paymentArrangement.regularPaymentAmount)}${currency}`;
  }

  public getStartDateFormatted(): string {
    let result = '';

    if (!_.isNil(this.paymentArrangement.regularPaymentStartDate)) {
      result = (moment(this.paymentArrangement.regularPaymentStartDate)).format('D/M/YYYY');
    }

    return result;
  }

  public getValidationEmailAddressRegex(): string {
    return Common.getValidationEmailAddressRegex();
  }

  public onConfirmPreviousButtonClick(): void {
    this.showWizardScreen = true;
    this.showConfirmScreen = false;
  }

  public onConfirmNextButtonClick(): void {
    this.savePaymentPlan();
  }

  private savePaymentPlan(): void {
    this.sessionService.showProcessing();

    this.paymentPlanService.createPaymentPlan(this.getFormDataObject()).subscribe({
      next: () => this.savePaymentPlanOk(),
      error: (error) => this.savePaymentPlanError(error.error)
    });
  }

  private savePaymentPlanOk(): void {
    this.sessionService.hideProcessing();

    let linkSuffix = '';

    if (this.selectedPaymentMethod === 'DD') {
      linkSuffix = 'arrangementdirectdebit';
    } else if (this.selectedPaymentMethod === 'MANUAL') {
      if (this.selectedPaymentCountry === 'Australia') {
        linkSuffix = 'arrangementmanualau';
      } else {
        linkSuffix = 'arrangementmanualnz';
      }
    } else if (this.selectedPaymentMethod === 'BPAY') {
      linkSuffix = 'arrangementbpayau';
    }

    this.router.navigate([`/payment/success/${linkSuffix}`]);
  }

  private savePaymentPlanError(error: any) {
    this.sessionService.hideProcessing();
    this.sessionService.showErrorDialog(error);
  }

  // We need to map the payment arrangement values to a collexus friendly format
  private getFormDataObject(): any {
    const dataObject: any = {};

    // Set the accounts
    dataObject.accounts = [];

    this.getSelectedDebtAccounts().forEach((account) => {
      dataObject.accounts.push({
        accountId: account.accountId,
        agreedAmt: account.totalOutstanding
      })
    });

    // Set base values
    if (!_.isNil(this.paymentPlanSummary?.payRefKey)) {
      dataObject.payRefKey = this.paymentPlanSummary.payRefKey;
    }

    dataObject.payMethod = this.selectedPaymentMethod;
    dataObject.countryId = this.getSelectedCountryId();
    dataObject.trackingCurrency = this.getPaymentCurrency();
    dataObject.instAmt = this.paymentArrangement.regularPaymentAmount;
    dataObject.instPaymentCurrency = this.getSelectedPaymentMethodCurrency();
    dataObject.firstInstDate = moment(this.paymentArrangement.regularPaymentStartDate).format('YYYY-MM-DD');
    dataObject.frequency = this.paymentArrangement.regularPaymentFrequencyPeriod[0];
    dataObject.declarations = [];


    // Specific form values
    const paymentMethod = this.getSelectedPaymentMethodObject();
    if (_.some(paymentMethod?.forms, (form) => form?.formId === 'PPYOURDETAILS')) {
      dataObject.firstName = this.formValues.firstName;
      dataObject.lastName = this.formValues.lastName;
      dataObject.emailAddr = this.formValues.emailAddress;
      dataObject.dateOfBirth = this.formValues.dateOfBirth;

      if (this.isIntercollVersion()) {
        dataObject.address = {
          line1: this.formValues.streetAddress,
          suburb: this.formValues.suburb,
          state: this.formValues.city,
          postcode: this.formValues.postcode,
          country: this.formValues.country
        };
      } else {
        dataObject.address = {
          line1: this.formValues.address?.line1,
          line2: this.formValues.address?.line2,
          line3: this.formValues.address?.line3,
          suburb: this.formValues.address?.suburb,
          state: this.formValues.address?.state,
          postcode: this.formValues.address?.postcode,
          country: this.getCountryId(this.formValues.address?.country),
          dpid: this.formValues.address?.dpid
      };
      }

      dataObject.mobPhoneNum = this.formValues.mobileNumber;
      dataObject.altphoneNum = this.formValues.alternativePhoneNumber;
      dataObject.winzClientNumber = this.formValues.winzClientNumber;
      dataObject.employerName = this.formValues.employerName;
    }

    if (_.some(paymentMethod?.forms, (form) => form?.formId === 'PPNEXTOFKIN')) {
      dataObject.nextOfKin = [
        {
          firstName: this.formValues.nok1FirstName,
          lastName: this.formValues.nok1LastName,
          phoneNumber: this.formValues.nok1PhoneNumber,
          relationship: this.formValues.nok1Relationship,
          discussConf: this.formValues.nok1Authorization
        },
        {
          firstName: this.formValues.nok2FirstName,
          lastName: this.formValues.nok2LastName,
          phoneNumber: this.formValues.nok2PhoneNumber,
          relationship: this.formValues.nok2Relationship
        }
      ];
    }

    if (_.some(paymentMethod?.forms, (form) => form?.formId === 'PPFINMENTOR')) {
      dataObject.finMentor = {
        orgName: this.formValues.fmOrganisationName,
        mentorName: this.formValues.fmMentorName,
        phoneNumber: this.formValues.fmPhoneNumber,
        emailAddr: this.formValues.fmEmail
      };
    }

    if (_.some(paymentMethod?.forms, (form) => form?.formId === 'PPBANKDETAILS')) {
      dataObject.bankDetails = {
        accName: this.formValues.bdAccountName,
        accBSB: this.formValues.bdAccountBSB,
        accNumber: this.formValues.bdAccountNumber,
        authSignatory: this.formValues.bdAuthorisedSignatory,
        authConf: this.formValues.bdAuthority
      };
    }

    if (_.some(paymentMethod?.forms, (form) => form?.formId === 'PPDECLARATIONS')) {
      Object.keys(this.formValues).forEach((key) => {
        if (key.startsWith('decValue')) {
          const declaration = _.find(this.paymentPlanDetails.declarations, (x) => x.decCode === key.replace('decValue', ''));
  
          if (!_.isNil(declaration)) {
            dataObject.declarations.push({
              decCode: declaration.decCode,
              decText: declaration.decText,
              decResponse: this.formValues[key]
            });
          }
        }
      });
    }
    
    return dataObject;
  }

  private initialisePaymentPlan(): void {
    this.sessionService.showProcessing();

    this.paymentPlanService.initialisePaymentPlan(this.getFormDataObject()).subscribe({
      next: (result) => this.initialisePaymentPlanOk(result),
      error: (error) => this.initialisePaymentPlanError(error.error)
    });
  }

  private initialisePaymentPlanOk(result: InitialisePaymentPlanResult): void {
    this.sessionService.hideProcessing();

    switch (this.selectedPaymentMethod) {
      case 'CARD':
        this.handleInitialiseCardPaymentPlan(result);
        break;
      default:
        this.paymentPlanSummary = result;
        this.showWizardScreen = false;
        this.showConfirmScreen = true;
    }
  }

  private initialisePaymentPlanError(error: any): void {
    this.sessionService.hideProcessing();
  }

  private handleInitialiseCardPaymentPlan(result: InitialisePaymentPlanResult): void {
    switch (result.supplierId) {
      case 'NAB':
        this.handleInitialiseNabCardPaymentPlan(result);
        break;
      case 'MW':
        this.handleInitialiseMwCardPaymentPlan(result);
        break;
      case 'PAYMARK':
        this.sessionService.hideProcessing();
        document.location.href = result.url ?? '';
        break;
    }
  }

  private handleInitialiseNabCardPaymentPlan(result: InitialisePaymentPlanResult): void {
    const paymentDialog = this.dialogService.open({
      content: NabCardPaymentDialogComponent
    });

    paymentDialog.result.subscribe((dialogResult: any) => {
      if (!_.isNil(dialogResult?.details)) {
        const cardDetails = dialogResult.details as CreditCardDetails;

        Common.createInitialisePaymentPlanNABDirectPostForm(result, cardDetails).then((form) => {
          document.body.appendChild(form);
          form.submit();
        });
      } else {
        this.sessionService.hideProcessing();
      }
    });
  }

  private handleInitialiseMwCardPaymentPlan(result: InitialisePaymentPlanResult): void {
    const paymentDialog = this.dialogService.open({
      content: MwCardPaymentDialogComponent
    });

    paymentDialog.result.subscribe((dialogResult: any) => {
      if (!_.isNil(dialogResult?.details)) {
        const cardDetails = dialogResult.details as CreditCardDetails;

        this.handleMwAddCard(result, cardDetails);
      } else {
        this.sessionService.hideProcessing();
      }
    });
  }

  private handleMwAddCard(result: InitialisePaymentPlanResult, cardDetails: CreditCardDetails): void {
    this.sessionService.showProcessing();

    this.paymentPlanService.merchantWarriorProcess(result.mwReferenceId ?? '', cardDetails).subscribe({
      next: () => {
        this.router.navigate(['/payment/success/arrangementmw']);
        this.sessionService.hideProcessing();
      },
      error: (error) => {
        this.sessionService.hideProcessing();
        this.sessionService.showErrorDialog(error?.error?.message ?? 'Error processing card. Please try again later.');
      }
    });
  }

  private getSelectedCountryId(): string {
    const selectedCountry = _.find(this.paymentPlanDetails.countries, (country) => country.countryName === this.selectedPaymentCountry);
    
    return selectedCountry?.countryId ?? '';
  }

  private getCountryId(name: string): string {
    const country = _.find(this.countries?.countries, (country) => country.countryName === name);
    
    return country?.countryId ?? name;
  }

  private getFormWizardTemplate(): FormWizardTemplate {
    const result = new FormWizardTemplate();

    switch (this.selectedPaymentMethod) {
      case 'DD':
        result.label = 'Direct debit.';
        result.description = `Please fill in your details below to pay your ${this.configurationService.getPaymentFormCompanyName()} debt by direct debit.`;
      break;
      case 'CARD':
        result.label = 'Debit card payment.';
        result.description = `Please fill in your details below to pay your ${this.configurationService.getPaymentFormCompanyName()} debt by debit card.`;
        break;
      case 'MANUAL':
        result.label = 'Bank Deposit payment.';
        result.description = `Please fill in your details below to pay your ${this.configurationService.getPaymentFormCompanyName()} debt by bank deposit.`;
        break;
      case 'BPAY':
        result.label = 'BPAY payment.';
        result.description = `Please fill in your details below to pay your ${this.configurationService.getPaymentFormCompanyName()} debt by BPAY.`;
        break;
    }

    this.getSelectedPaymentMethodObject()?.forms.forEach((form) => {
      const formWizardPage = this.createFormWizardPageForFormId(form.formId);
      if (!_.isNil(formWizardPage)) {
        result.pages.push(formWizardPage);
      }
    });

    return result;
  }

  private createFormWizardPageForFormId(formId: string): FormWizardPage | null {
    let result = null;

    switch (formId) {
      case 'PPYOURDETAILS':
        result = this.getYourDetailsPage();
        break;
      case 'PPNEXTOFKIN':
        result = this.getNextOfKinPage();
        break;
      case 'PPFINMENTOR':
        result = this.getFinancialMentorPage();
        break;
      case 'PPBANKDETAILS':
        result = this.getDirectDebitBankDetailsFormWizardPage();
        break;
      case 'PPDECLARATIONS':
        result = this.getDeclarationsFormWizardPage();
        break;
    }

    return result;
  }

  private getYourDetailsPage(): FormWizardPage {
    const result = {
      label: 'Your details',
      previousButtonLabel: 'Previous',
      nextButtonLabel: 'Next',
      completeButtonLabel: '',
      elements: [
        {
          type: 'heading',
          name: '',
          label: 'Your details',
          class: 'col-12',
          required: false,
        },
        {
          type: 'textInput',
          name: 'firstName',
          label: 'First name',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'lastName',
          label: 'Last name',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'emailAddress',
          label: 'Email address',
          class: 'col-12',
          required: true,
          defaultValue: '',
          pattern: Common.getValidationEmailAddressRegex()
        },
        {
          type: 'textInput',
          name: 'mobileNumber',
          label: 'Mobile number',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: '',
          pattern: Common.getValidationMobileNumberRegex()
        },
        {
          type: 'textInput',
          name: 'alternativePhoneNumber',
          label: 'Alternative phone number',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: '',
          pattern: Common.getValidationMobileNumberRegex()
        },
        {
          type: 'dateInput',
          name: 'dateOfBirth',
          label: 'Date of birth',
          pattern: 'dd/MM/yyyy',
          class: 'col-12 col-md-4',
          required: true,
          defaultValue: '',
          maxValue: new Date()
        },
        ((!this.isIntercollVersion()) && {
          type: 'addressInput',
          name: 'address',
          label: 'Address',
          class: 'col-12 col-md-8',
          required: true,
          defaultSearchCountry: this.paymentPlanDetails.defaultCountryId ?? this.getSelectedCountryId()
        }),
        (this.isIntercollVersion() && {
          type: 'textInput',
          name: 'streetAddress',
          label: 'Street address',
          class: 'col-12 col-md-8',
          required: true,
          defaultValue: ''
        }),
        (this.isIntercollVersion() && {
          type: 'textInput',
          name: 'suburb',
          label: 'Suburb',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        }),
        (this.isIntercollVersion() && {
          type: 'textInput',
          name: 'city',
          label: this.isIntercollAustralianVersion() ? 'State or City/Region' : 'City/Region/State',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        }),
        (this.isIntercollVersion() && {
          type: 'textInput',
          name: 'postcode',
          label: 'Postcode',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        }),
        (this.isIntercollVersion() && {
          type: 'dropDownListInput',
          name: 'country',
          label: 'Country',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: this.getSelectedCountryId() !== 'OTHER' ? this.getSelectedCountryId() : '',
          values: _.sortBy(this.countries.countries, (country) => country.countryName),
          valueField: 'countryId',
          nameField: 'countryName'
        }),
        ((this.getSelectedCountryId() === 'NZ' || this.isIntercollVersion()) && {
          type: 'textInput',
          name: 'winzClientNumber',
          label: this.isIntercollAustralianVersion() ? 'Centrelink Customer Reference Number (CRN)' : 'WINZ Client No. (if applicable)',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: ''
        }),
        {
          type: 'textInput',
          name: 'employerName',
          label: this.isIntercollAustralianVersion() ? 'Employer name/Income source' : 'Employer Name',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        },
        {
          type: 'separator',
          name: '',
          label: '',
          class: 'col-12',
          required: false
        }
      ]
    } as FormWizardPage;

    return result;
  }

  public getNextOfKinPage(): FormWizardPage {
    const result = {
      label: 'Next of Kin',
      previousButtonLabel: 'Previous',
      nextButtonLabel: 'Next',
      completeButtonLabel: '',
      elements: [
        {
          type: 'heading',
          name: '',
          label: 'Your Next of Kin #1',
          class: 'col-12',
          required: false,
        },
        {
          type: 'textInput',
          name: 'nok1FirstName',
          label: 'First name',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'nok1LastName',
          label: 'Last name',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'nok1PhoneNumber',
          label: 'Phone Number',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: '',
          pattern: Common.getValidationMobileNumberRegex()
        },                {
          type: 'textInput',
          name: 'nok1Relationship',
          label: 'Relationship',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        },
        {
          type: 'separator',
          name: '',
          label: '',
          class: 'col-12',
          required: false
        },
        {
          type: 'heading',
          name: '',
          label: 'Your Next of Kin #2',
          class: 'col-12',
          required: false,
        },
        {
          type: 'textInput',
          name: 'nok2FirstName',
          label: 'First name',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'nok2LastName',
          label: 'Last name',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'nok2PhoneNumber',
          label: 'Phone Number',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: '',
          pattern: Common.getValidationMobileNumberRegex()
        },
        {
          type: 'textInput',
          name: 'nok2Relationship',
          label: 'Relationship',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: ''
        },
        {
          type: 'separator',
          name: '',
          label: '',
          class: 'col-12',
          required: false
        }
      ]
    } as FormWizardPage;

    return result;
  }

  private getFinancialMentorPage(): FormWizardPage {
    const result = {
      label: 'Financial mentor (if applicable)',
      previousButtonLabel: 'Previous',
      nextButtonLabel: 'Next',
      completeButtonLabel: '',
      elements: [
        {
          type: 'heading',
          name: '',
          label: 'Financial mentor (if applicable)',
          class: 'col-12',
          required: false,
        },
        {
          type: 'paragraph',
          name: '',
          label: 'By adding a financial mentor you\'re authorising Debt Managers to discuss details of your account/s with the named organisation.',
          class: 'col-12',
          required: false,
        },
        {
          type: 'textInput',
          name: 'fmOrganisationName',
          label: 'Organisation name',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'fmMentorName',
          label: 'Mentor\'s name',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'fmPhoneNumber',
          label: 'Phone Number',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: ''
        },
        {
          type: 'textInput',
          name: 'fmEmail',
          label: 'Email',
          class: 'col-12 col-md-6',
          required: false,
          defaultValue: '',
          pattern: Common.getValidationEmailAddressRegex()
        },
        {
          type: 'separator',
          name: '',
          label: '',
          class: 'col-12',
          required: false
        }
      ]
    } as FormWizardPage;

    return result;
  }

  private getDirectDebitBankDetailsFormWizardPage(): FormWizardPage
  {
    const page = {
      label: 'Your Bank Details',
      previousButtonLabel: 'Previous',
      nextButtonLabel: 'Next',
      completeButtonLabel: '',
      elements: [
        {
          type: 'heading',
          name: '',
          label: 'Your Bank Details',
          class: 'col-12',
          required: false,
        },
        {
          type: 'textInput',
          name: 'bdAccountName',
          label: 'Name of account holder',
          class: 'col-12 col-md-6',
          required: true,
          defaultValue: ''
        },
        {
          type: 'empty',
          name: 'bdSpacer',
          class: 'd-none d-md-block col-md-6'
        }
      ]
    } as FormWizardPage;

    if (this.selectedPaymentCountry === 'Australia') {
      page.elements.push({
        type: 'textInput',
        name: 'bdAccountBSB',
        label: 'BSB',
        class: 'col-12 col-md-6',
        required: true,
        defaultValue: '',
        pattern: Common.getValidationBankBSBRegex()
      } as FormWizardPageElement);
      page.elements.push({
        type: 'textInput',
        name: 'bdAccountNumber',
        label: 'Account number',
        class: 'col-12 col-md-6',
        required: true,
        defaultValue: '',
        pattern: Common.getValidationBankAccountNumberRegex(this.selectedPaymentCountry)
      } as FormWizardPageElement);
    } else {
      page.elements.push({
        type: 'maskedTextInput',
        name: 'bdAccountNumber',
        label: 'Account number',
        class: 'col-12 col-md-6',
        required: true,
        defaultValue: '',
        mask: '00-0000-0000000-009',
        pattern: '[0-9]{15,16}',
        validatorFunction: this.isIntercollVersion() && !this.isIntercollAustralianVersion() ? this.intercollNZBankAccountValidation.bind(this) :
          null
      } as FormWizardPageElement);
      page.elements.push(        {
        type: 'empty',
        name: 'bdSpacer',
        class: 'd-none d-md-block col-md-6'
      } as FormWizardPageElement);
    }

    if (this.isIntercollAustralianVersion()) {
      page.elements.push({
        type: 'html',
        name: '',
        label: 'By signing and/or providing us with a valid instruction in respect to your Direct Debit Request, you confirm you have understood and agreed to the terms and conditions governing the debit arrangements between you and DebtManagers Pty Limited, User No: 643311. You also authorise us to debit your nominated bank account as set out in this request in accordance with the Direct Debit Request Service Agreement (that can be viewed <a href="https://www.debtmanagers.com.au/direct-debit-terms-au" target="_blank">here</a>).<br>The debit or charge will be made through the Bulk Electronic Clearing System (BECS) from your account held at the financial institution you have nominated above.',
        class: 'col-12',
        required: false,
      } as FormWizardPageElement);
    }

    page.elements.push({
      type: 'textInput',
      name: 'bdAuthorisedSignatory',
      label: 'Name of authorised signatory',
      class: 'col-12 col-md-6',
      required: true,
      defaultValue: ''
    } as FormWizardPageElement);
    page.elements.push({
        type: 'separator',
        name: '',
        label: '',
        class: 'col-12',
        required: false
      } as FormWizardPageElement);

    return page;
  }

  private getDeclarationsFormWizardPage(): FormWizardPage {
    const page = new FormWizardPage();
    page.label = 'Declarations';
    page.previousButtonLabel = 'Previous';
    page.nextButtonLabel = '';
    page.completeButtonLabel = 'Next';

    page.elements.push({
      type: 'heading',
      name: '',
      label: 'Declaration',
      class: 'col-12',
      required: false,
    } as FormWizardPageElement);

    this.getDeclarationsPageElements().forEach((element) => {
      page.elements.push(element);
    });

    page.elements.push({
      type: 'separator',
      name: '',
      label: '',
      class: 'col-12',
      required: false
    } as FormWizardPageElement);

    return page;
  }

  private getDeclarationsPageElements(): Array<FormWizardPageElement> {
    const result: Array<FormWizardPageElement> = [];

    this.getSelectedPaymentMethodObject()?.declarations?.forEach((dec) => {
      const declaration = _.find(this.paymentPlanDetails.declarations, (decIteration) => decIteration.decCode === dec.decId);

      if (!_.isNil(declaration)) {
        result.push({
          type: 'checkbox',
          name: `decValue${declaration.decCode}`,
          label: declaration.decText,
          class: 'col-12 mt-2 mb-2',
          required: true
        } as FormWizardPageElement);
      }
    });

    if (result.length > 1) {
      result.unshift({
        type: 'selectAll',
        name: _.map(result, (element) => element.name).join(),
        label: 'Select All',
        class: 'col-12 mt-2 mb-2',
        required: false
      } as FormWizardPageElement);
    }

    return result;
  }

  intercollNZBankAccountValidation(value: string) {
  let result = '';

  if (!_.isNil(value) && value !== '') {
    if (value.length >= 15 && (value.startsWith('4') || value.startsWith('5'))) {
      result = 'Please enter your NZ bank account number, not your card number.';
    } else {
      const invalidBankAccountNumbers = this.appConfigService.getConfig().defaultConfiguration.invalidBankAccountNumbers;

      if (!_.isNil(invalidBankAccountNumbers) && _.some(invalidBankAccountNumbers, (x) => x === value)) {
        result = 'Bank account not valid. Please enter the bank account you are authorised on.';
      }
    }
  }

  return result;
}
}
